/**
 * Trading window of a game's asset. The games run on Deriv's 1s synthetic indices, which trade
 * 24/7: no status is written today, so nothing is blocked (an unknown status never blocks). Kept
 * for a game moved to an asset with market hours.
 */
export interface MarketStatus {
  symbol: string;
  open: boolean;
  /** The broker suspended the asset outside its schedule: the reopening is unknown. */
  suspended: boolean;
  /** Epoch s the open block ends (null: closed, or no end in the schedule). */
  closesAt: number | null;
  /** Epoch s a closed asset reopens (null: open, or unknown). */
  opensAt: number | null;
  /** Epoch ms the feed read it. */
  at: number;
}

/** A status older than this is unknown (the feed stopped refreshing it): nothing is blocked on it. */
export const MARKET_STATUS_MAX_AGE_MS = 60_000;
/** An entry must end this long before the market closes. */
export const MARKET_CLOSE_MARGIN_S = 5;
/** The games warn this long before a pause. */
export const MARKET_CLOSE_WARN_S = 5 * 60;

export type MarketBlock = 'CLOSED' | 'CLOSING';

/**
 * Why an entry starting at `startS` and lasting `lengthS` can't be placed now: the market is closed,
 * or it closes before the entry ends (plus a margin). Null when it can — or when the status is unknown.
 */
export function marketBlock(s: MarketStatus | null | undefined, startS: number, lengthS: number, nowMs = Date.now()): MarketBlock | null {
  if (!s || nowMs - s.at > MARKET_STATUS_MAX_AGE_MS) return null;
  if (!s.open) return 'CLOSED';
  if (s.closesAt !== null && startS + lengthS + MARKET_CLOSE_MARGIN_S > s.closesAt) return 'CLOSING';
  return null;
}

/** "05:30" in Brasília time; with the date ("24/10 05:30") when it is more than 12 hours away. */
export function brasiliaClock(epochS: number, nowMs = Date.now()): string {
  const far = epochS * 1000 - nowMs > 12 * 3600_000;
  return new Date(epochS * 1000).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    ...(far ? { day: '2-digit', month: '2-digit' } : {}),
  });
}

/** The pause in a few words: "em pausa na corretora até 05:30". */
export function marketPauseText(s: Pick<MarketStatus, 'opensAt'>, nowMs = Date.now()): string {
  return s.opensAt !== null ? `em pausa na corretora até ${brasiliaClock(s.opensAt, nowMs)}` : 'em pausa na corretora, sem horário de volta informado';
}
